import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, Typography, Tabs, Tab, TextField, Alert,
  CircularProgress, Card, CardContent, Chip, Autocomplete,
  Divider, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { saveCarteJson } from '../utils/api';
import BackupManager from '../components/BackupManager';

const navStyle = { display: 'flex', gap: 1, mb: 3, borderBottom: '1px solid #eee', pb: 2 };

// Mapping: clé dans options → clé dans categories
const OPTION_TO_CATEGORY = {
  entrées: 'entrées',
  plats: 'plats',
  garnitures: 'garniture',
  desserts: 'desserts',
};

const DashboardMenus = () => {
  const navigate = useNavigate();
  const [user] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('user')); } catch { return null; }
  });
  const [carte, setCarte] = useState(null);
  const [tab, setTab] = useState('menus');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/dashboard'); return; }
    fetch(`/assets/json/carte.json?v=${Date.now()}`)
      .then(r => r.json())
      .then(data => setCarte(data))
      .catch(() => setMessage({ type: 'error', text: 'Erreur chargement carte.json' }));
  }, [user, navigate]);

  // Retourne les items d'une catégorie indexés par id
  const getItemsById = (catKey) => {
    const list = carte?.categories?.[catKey] || [];
    const map = {};
    list.forEach(item => { if (item.id != null) map[item.id] = item; });
    return map;
  };

  const updateMenu = (idx, field, value) => {
    setCarte(prev => {
      const menus = [...prev.menus];
      menus[idx] = { ...menus[idx], [field]: value };
      return { ...prev, menus };
    });
  };

  const updateMenuOption = (menuIdx, optionKey, ids) => {
    setCarte(prev => {
      const menus = [...prev.menus];
      menus[menuIdx] = {
        ...menus[menuIdx],
        options: { ...menus[menuIdx].options, [optionKey]: ids }
      };
      return { ...prev, menus };
    });
  };

  const deleteMenu = (idx) => {
    setCarte(prev => ({ ...prev, menus: prev.menus.filter((_, i) => i !== idx) }));
  };

  const addMenu = () => {
    const maxId = (carte.menus || []).reduce((m, menu) => Math.max(m, menu.id || 0), 0);
    setCarte(prev => ({
      ...prev,
      menus: [...(prev.menus || []), {
        id: maxId + 1,
        nom: 'Nouveau menu',
        prix: 0,
        description: '',
        options: { entrées: [], plats: [], garnitures: [], desserts: [] },
        includes: []
      }]
    }));
  };

  const updatePicker = (idx, field, value) => {
    setCarte(prev => {
      const pickers = [...prev['menus-picker']];
      pickers[idx] = { ...pickers[idx], [field]: value };
      return { ...prev, 'menus-picker': pickers };
    });
  };

  const updatePickerAll = (idx, ids) => {
    setCarte(prev => {
      const pickers = [...prev['menus-picker']];
      pickers[idx] = {
        ...pickers[idx],
        options: { ...pickers[idx].options, all: ids }
      };
      return { ...prev, 'menus-picker': pickers };
    });
  };

  const deletePicker = (idx) => {
    setCarte(prev => ({ ...prev, 'menus-picker': prev['menus-picker'].filter((_, i) => i !== idx) }));
  };

  const addPicker = () => {
    const maxId = (carte['menus-picker'] || []).reduce((m, p) => Math.max(m, p.id || 0), 0);
    setCarte(prev => ({
      ...prev,
      'menus-picker': [...(prev['menus-picker'] || []), {
        id: maxId + 1,
        nom: 'Nouveau menu picker',
        prix: 0,
        description: '',
        options: { all: [] },
        nb: [2]
      }]
    }));
  };

  const reloadCarte = () => {
    fetch(`/assets/json/carte.json?v=${Date.now()}`)
      .then(r => r.json())
      .then(data => setCarte(data));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveCarteJson(carte, user);
      setMessage({ type: 'success', text: 'Carte sauvegardée avec succès !' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Erreur lors de la sauvegarde' });
    }
    setSaving(false);
  };

  if (!user) return null;
  if (!carte) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const menus = carte.menus || [];
  const pickers = carte['menus-picker'] || [];

  // Pour menus-picker, on utilise la garniture comme pool d'items (IDs 1-13)
  const garnitureItems = Object.values(getItemsById('garniture'));

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={navStyle}>
        <Button component={Link} to="/dashboard" variant="outlined" size="small">Commandes</Button>
        <Button component={Link} to="/dashboard/plats" variant="outlined" size="small">Plats</Button>
        <Button variant="contained" size="small" disabled>Menus</Button>
        <Button component={Link} to="/dashboard/categories" variant="outlined" size="small">Catégories</Button>
        <Button component={Link} to="/dashboard/reglages" variant="outlined" size="small">Fermeture / Vacances</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" color="error" onClick={() => { sessionStorage.removeItem('user'); navigate('/dashboard'); }}>
          Déconnexion
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>Modifier les menus</Typography>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Menus" value="menus" />
        <Tab label="Menus Picker" value="pickers" />
      </Tabs>

      {tab === 'menus' && (
        <Box>
          {menus.map((menu, menuIdx) => {
            return (
              <Card key={menu.id} sx={{ mb: 3 }} variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6">Menu #{menu.id}</Typography>
                    <IconButton size="small" color="error" onClick={() => deleteMenu(menuIdx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      label="Nom" value={menu.nom || ''} size="small" sx={{ flex: 2 }}
                      onChange={e => updateMenu(menuIdx, 'nom', e.target.value)}
                    />
                    <TextField
                      label="Prix (€)" value={menu.prix ?? ''} size="small" type="number" sx={{ width: 100 }}
                      inputProps={{ step: '0.5', min: '0' }}
                      onChange={e => updateMenu(menuIdx, 'prix', parseFloat(e.target.value) || 0)}
                    />
                  </Box>

                  <TextField
                    label="Description" value={menu.description || ''} size="small" fullWidth sx={{ mb: 2 }}
                    onChange={e => updateMenu(menuIdx, 'description', e.target.value)}
                  />

                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Items disponibles par catégorie</Typography>

                  {Object.entries(OPTION_TO_CATEGORY).map(([optKey, catKey]) => {
                    const itemsById = getItemsById(catKey);
                    const allItems = Object.values(itemsById);
                    const selectedIds = menu.options?.[optKey] || [];
                    const selectedItems = selectedIds.map(id => itemsById[id]).filter(Boolean);

                    return (
                      <Box key={optKey} sx={{ mb: 2 }}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={allItems}
                          value={selectedItems}
                          getOptionLabel={item => `[${item.ref}] ${item.nom}`}
                          isOptionEqualToValue={(opt, val) => opt.id === val.id}
                          onChange={(_, newVal) => updateMenuOption(menuIdx, optKey, newVal.map(i => i.id))}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={option.id}
                                label={`${option.ref} - ${option.nom}`}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={`${optKey.charAt(0).toUpperCase() + optKey.slice(1)} disponibles`}
                              placeholder="Ajouter un item…"
                            />
                          )}
                        />
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}

          <Button variant="outlined" startIcon={<AddIcon />} onClick={addMenu} sx={{ mb: 3 }}>
            Ajouter un menu
          </Button>
        </Box>
      )}

      {tab === 'pickers' && (
        <Box>
          {pickers.map((picker, pickerIdx) => (
            <Card key={picker.id} sx={{ mb: 3 }} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6">Picker #{picker.id}</Typography>
                  <IconButton size="small" color="error" onClick={() => deletePicker(pickerIdx)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Nom" value={picker.nom || ''} size="small" sx={{ flex: 2 }}
                    onChange={e => updatePicker(pickerIdx, 'nom', e.target.value)}
                  />
                  <TextField
                    label="Prix (€)" value={picker.prix ?? ''} size="small" type="number" sx={{ width: 100 }}
                    inputProps={{ step: '0.5', min: '0' }}
                    onChange={e => updatePicker(pickerIdx, 'prix', parseFloat(e.target.value) || 0)}
                  />
                  <TextField
                    label="Nb items à choisir" value={picker.nb?.[0] ?? ''} size="small" type="number" sx={{ width: 140 }}
                    inputProps={{ min: '1' }}
                    onChange={e => updatePicker(pickerIdx, 'nb', [parseInt(e.target.value) || 1])}
                  />
                </Box>

                <TextField
                  label="Description" value={picker.description || ''} size="small" fullWidth sx={{ mb: 2 }}
                  onChange={e => updatePicker(pickerIdx, 'description', e.target.value)}
                />

                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Items disponibles au choix</Typography>

                <Autocomplete
                  multiple
                  size="small"
                  options={garnitureItems}
                  value={garnitureItems.filter(item => (picker.options?.all || []).includes(item.id))}
                  getOptionLabel={item => `[${item.ref}] ${item.nom}`}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  onChange={(_, newVal) => updatePickerAll(pickerIdx, newVal.map(i => i.id))}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.id}
                        label={`${option.ref} - ${option.nom}`}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Items disponibles" placeholder="Ajouter un item…" />
                  )}
                />
              </CardContent>
            </Card>
          ))}

          <Button variant="outlined" startIcon={<AddIcon />} onClick={addPicker} sx={{ mb: 3 }}>
            Ajouter un menu picker
          </Button>
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving} size="large">
          {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
        </Button>
        <BackupManager user={user} onRestore={reloadCarte} />
      </Box>
    </Box>
  );
};

export default DashboardMenus;
